import React, { useState, useEffect, useCallback } from 'react'
import { Box, Typography, CircularProgress, Button } from '@mui/material'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { adminTeamsService } from '../../../services/admin/teams'
import { downloadExcel } from '../../../utility/excelDownloadUtils'
import AddEditUserDialog from './AddEditUserDialog'
import UserFilters from './components/UserFilters'
import UserTable from './components/UserTable'
import ExportColumnsDialog from './components/ExportColumnsDialog'

const DEFAULT_FILTERS = {
  search: '',
  category: null,
  user_type: null,
  work_status: null,
  is_account_active: '',
}

const DEFAULT_COLUMN_VISIBILITY = {
  user_id: false,        // add this
  first_name: false,    // hidden by default
  middle_name: false,   // hidden by default
  last_name: false,     // hidden by default
  department: true,
  designation: false,
  sub_department: true,
  gender: true,
  blood_group: false,
  is_admin: false,
  primary_contact: false,
  office_contact: false,
  date_of_joining: false,
  date_of_birth: false,
  date_of_leaving: false,
  notice_period_end_date: false,
  probation_end_date: false,
  permanent_address: false,
  temporary_address: false,
  aadhar_number: false,
  pan_number: false,
  short_bio: false,
  reporting_manager_id: true,
}

const normalizeBoolean = (value) =>
  value === true || value === 'true' || value === 1 || value === '1'

const EXPORT_COLUMNS = [
  { key: 'first_name', label: 'First Name', width: 20 },
  { key: 'middle_name', label: 'Middle Name', width: 20 },
  { key: 'last_name', label: 'Last Name', width: 20 },
  { key: 'email', label: 'Email', width: 30 },
  { key: 'employee_id', label: 'Employee ID', width: 15 },
  { key: 'department_name', label: 'Department', width: 20 },
  { key: 'sub_department_name', label: 'Sub Department', width: 20 },
  { key: 'designation_name', label: 'Designation', width: 20 },
  { key: 'category_name', label: 'Category', width: 20 },
  { key: 'user_type_name', label: 'User Type', width: 15 },
  { key: 'work_status_name', label: 'Work Status', width: 15 },
  { key: 'gender_name', label: 'Gender', width: 15 },
  { key: 'blood_group_name', label: 'Blood Group', width: 15 },
  { key: 'aadhar_number', label: 'Aadhar Number', width: 20 },
  { key: 'pan_number', label: 'PAN Number', width: 20 },
  { key: 'primary_contact', label: 'Primary Contact', width: 20 },
  { key: 'office_contact', label: 'Office Contact', width: 20 },
  { key: 'date_of_joining', label: 'Date of Joining', width: 20, format: (v) => v ? String(v).split('T')[0] : '' },
  { key: 'date_of_birth', label: 'Date of Birth', width: 20, format: (v) => v ? String(v).split('T')[0] : '' },
  { key: 'date_of_leaving', label: 'Date of Leaving', width: 20, format: (v) => v ? String(v).split('T')[0] : '' },
  { key: 'notice_period_end_date', label: 'Notice Period End Date', width: 20, format: (v) => v ? String(v).split('T')[0] : '' },
  { key: 'probation_end_date', label: 'Probation End Date', width: 20, format: (v) => v ? String(v).split('T')[0] : '' },
  { key: 'is_admin', label: 'Admin', width: 10, format: (v) => String(v) === 'true' || v === true ? 'Yes' : 'No' },
  { key: 'is_account_active', label: 'Account Active', width: 15, format: (v) => String(v) === 'true' || v === true ? 'Yes' : 'No' },
]

const UserManagement = () => {
  const { user } = useAuthContext()

  const [users, setUsers] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [listConfigs, setListConfigs] = useState({})

  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(DEFAULT_COLUMN_VISIBILITY)

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })

  const [sortModel, setSortModel] = useState([{ field: 'first_name', sort: 'asc' }])

  const [editingUser, setEditingUser] = useState(null)
  const [allUsers, setAllUsers] = useState([])

  useEffect(() => {
    if (!user) return
    adminTeamsService
      .getAllCompanyListConfigs(user)
      .then((data) => setListConfigs(data || {}))
      .catch(console.error)
  }, [user])

  const fetchAllUsers = useCallback(() => {
    if (!user) return
    adminTeamsService.getUsers(user).then(setAllUsers).catch(console.error)
  }, [user])

  useEffect(() => {
    fetchAllUsers()
  }, [fetchAllUsers])

  const fetchUsers = useCallback(() => {
    if (!user) return
    setLoading(true)

    const params = {
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v != null && v !== '')),
      page: paginationModel.page + 1,
      page_size: paginationModel.pageSize,
    }

    if (sortModel.length > 0) {
      params.sort_by = sortModel[0].field
      params.sort_order = sortModel[0].sort
    }

    adminTeamsService
      .getAdminUsers(user, params)
      .then(({ users: rows, total: t }) => {
        const normalizedRows = (rows || []).map((row) => ({
          ...row,
          is_admin: normalizeBoolean(row?.is_admin),
          is_account_active: normalizeBoolean(row?.is_account_active),
        }))

        const dedupedRows = Array.from(
          normalizedRows.reduce((map, row) => map.set(row.id, row), new Map()).values(),
        )

        setUsers(dedupedRows)
        setTotal(t)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [user, filters, paginationModel, sortModel])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleFilterChange = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }, [])

  const handleSortModelChange = useCallback((model) => {
    setSortModel(model)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }, [])

  const handleExport = useCallback(async (selectedColumnKeys) => {
    if (!user) return
    setExportLoading(true)

    try {
      const params = {
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v != null && v !== '')),
        export_all: true,
      }

      if (sortModel.length > 0) {
        params.sort_by = sortModel[0].field
        params.sort_order = sortModel[0].sort
      }

      const { users: rows } = await adminTeamsService.getAdminUsers(user, params)

      const activeColumns = EXPORT_COLUMNS.filter((col) => selectedColumnKeys.includes(col.key))
      const letters = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z']

      const headerRow = {}
      const columnsWidth = {}
      activeColumns.forEach((col, i) => {
        headerRow[letters[i]] = { v: col.label, s: { bold: true } }
        columnsWidth[letters[i]] = col.width
      })

      const dataForExcel = rows.map((row) => {
        const rowData = {}
        activeColumns.forEach((col, i) => {
          let val = row[col.key]
          if (col.format) val = col.format(val)
          if (val == null) val = ''
          rowData[letters[i]] = { v: val }
        })
        return rowData
      })

      const sheetData = [
        {
          name: 'Users',
          data: [headerRow, ...dataForExcel],
          columnsWidth: columnsWidth
        }
      ]

      downloadExcel(sheetData, 'Users_Export')
      setExportDialogOpen(false)

    } catch (error) {
      console.error(error)
    } finally {
      setExportLoading(false)
    }
  }, [user, filters, sortModel])

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <PeopleAltIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          User Management
        </Typography>

        {!loading && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({total} users)
          </Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="outlined"
          size="small"
          onClick={() => setExportDialogOpen(true)}
          disabled={exportLoading}
          startIcon={<GetAppOutlinedIcon />}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Export
        </Button>

        <Button
          variant="contained"
          size="small"
          onClick={() => setEditingUser({})}
          sx={{ whiteSpace: 'nowrap' }}
        >
          + Add User
        </Button>
      </Box>

      <UserFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        listConfigs={listConfigs}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
      />

      {loading && users.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <UserTable
          rows={users}
          total={total}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          listConfigs={listConfigs}
          allUsers={allUsers}
          onEditClick={(row) => setEditingUser(row)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
        />
      )}

      <AddEditUserDialog
        isOpen={editingUser !== null}
        setOpenClose={(open) => {
          if (!open) setEditingUser(null)
        }}
        initialUser={editingUser || null}
        users={allUsers}
        designationListData={listConfigs?.designation?.items || []}
        departmentListData={listConfigs?.department?.items || []}
        subfunctionListData={listConfigs?.sub_department?.items || []}
        categoryListData={listConfigs?.category?.items || []}
        userTypeListData={listConfigs?.user_type?.items || []}
        workStatusListData={listConfigs?.work_status?.items || []}
        genderListData={listConfigs?.gender?.items || []}
        bloodGroupListData={listConfigs?.blood_group?.items || []}
        fetchFreshList={() => {
          fetchUsers()
          fetchAllUsers()
        }}
      />

      <ExportColumnsDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        columns={EXPORT_COLUMNS}
        onConfirm={handleExport}
        loading={exportLoading}
      />
    </Box>
  )
}

export default UserManagement
